/**
 * Reddit-Ads-Pixel (cowork24.ch).
 *
 * Gegenstück zu `util/analytics.js` (GA4): Dieselben semantischen Ereignisse
 * werden zusätzlich an Reddit gemeldet, damit Reddit-Kampagnen auf echte
 * Conversions optimieren können. Die Zuordnung steht in `REDDIT_EVENTS` —
 * jedes GA4-Ereignis wird genau einmal gemeldet, ein Doppelzählen gibt es
 * also nicht.
 *
 * Geladen wird das Pixel erst nach `window` load + Idle-Phase (siehe
 * `util/DeferredAnalyticsScripts.js`), damit der Main-Thread beim First Paint
 * frei bleibt.
 *
 * **Einwilligung:** Anders als Google kennt Reddit keinen Consent Mode — ein
 * geladenes Pixel setzt sofort Cookies. Im EWR und im Vereinigten Königreich
 * wird das Skript deshalb gar nicht erst eingebunden, solange keine
 * Einwilligung vorliegt (siehe `isRedditTrackingAllowed`). Die Länder-Logik
 * kommt aus `util/consent.js`.
 *
 * Ohne `REACT_APP_REDDIT_PIXEL_ID` ist die gesamte Datei ein No-Op.
 */
import { redditPixelId } from '../config/configAnalytics';
import { CONSENT_GRANTED, isConsentRequiredRegion, readConsent } from './consent';

const SCRIPT_ID = 'deferred-reddit-pixel-js';
const SCRIPT_SRC = 'https://www.redditstatic.com/ads/pixel.js';

/**
 * Sentinel für Ereignisse ohne passendes Reddit-Standardereignis: Sie gehen als
 * `Custom` raus, wobei der `customEventName` exakt der GA4-Name ist. So bleibt
 * die Taxonomie zwischen GA4 und Reddit 1:1 und lesbar im Events Manager.
 */
const CUSTOM = Symbol('reddit-custom-event');

/**
 * GA4-Ereignis → Reddit-Ereignis.
 *
 * Bewusst NICHT gemeldet: `login`, `cta_click` und `badge_tooltip_open` —
 * reine Interaktionssignale in hoher Frequenz, die im Events Manager nur die
 * Conversion-Auswahl zumüllen. `badge_tooltip_open` feuert schon beim blossen
 * Überfahren des Siegels, und eine Inseratsseite trägt zwei davon: ein Zeiger,
 * der die Seite überstreicht, erzeugt mehrere Ereignisse pro Besuch. Ein
 * bewusster Klick (`cta_click`) ist nach genau dieser Regel ausgeschlossen —
 * ein Hover erfüllt sie noch deutlicher.
 *
 * Zu `Lead`: `generate_lead` ist die ANGEBOTSSEITE (Space-Betreiber) und behält
 * das Standardereignis. Die Nachfrageseite (`contact_host`) läuft absichtlich
 * als eigenes Custom-Event — dieselbe Trennung wie in GA4/Google Ads, damit ein
 * B2C-Lead nie die B2B-Lead-Optimierung verwässert.
 */
const REDDIT_EVENTS = {
  sign_up: 'SignUp',
  view_item: 'ViewContent',
  search: 'Search',
  begin_checkout: 'AddToCart',
  // Hauptconversion: Buchungsanfrage (beide Flows — Standard-Checkout und
  // Soft-Booking — feuern `booking_request`, siehe CLAUDE.md).
  booking_request: 'Purchase',
  subscription_start: 'Purchase',
  generate_lead: 'Lead',
  // Absichtssignal der Angebotsseite. BEWUSST `Custom` und NICHT `Lead`: Ein
  // Klick auf „Space inserieren" ist keine Anfrage. Würde er als `Lead` zählen,
  // lernte Reddits Gebotsmodell, Knopfdrücker zu finden, und die Kosten pro Lead
  // wären bedeutungslos — derselbe Fehler, der bei Google Ads schon einmal
  // Schein-Conversions erzeugt hat (siehe CLAUDE.md, „Registrierung
  // (Seitenaufbau /signup)"). Als Custom-Ereignis ist es trotzdem
  // Retargeting-Zielgruppe und im Bericht sichtbar.
  list_space_intent: CUSTOM,
  contact_host: CUSTOM,
  listing_start: CUSTOM,
  listing_published: CUSTOM,
  // `booking_authorized` läuft im Standard-Checkout zusätzlich zu
  // `booking_request` — deshalb Custom statt eines zweiten `Purchase`.
  booking_authorized: CUSTOM,
  booking_confirmed: CUSTOM,
  lounge_booking_click: CUSTOM,
  // Concierge-Suche: BEWUSST Custom und nicht `Search` — die Ergebnisseite
  // feuert beim Laden ohnehin `search` (→ Reddit `Search`); ein zweites
  // Search-Ereignis pro Concierge-Anfrage wäre eine Doppelzählung.
  ai_concierge_search: CUSTOM,
  // Badge und Anbieter-Stupser: durchgehend Custom.
  // Eine eingeblendete Flaeche, ein geoeffneter Tooltip und ein Klick auf einen
  // internen Hinweis sind KEINE Anfrage — als Lead gezaehlt lernte das
  // Gebotsmodell, Knopfdruecker zu finden (dieselbe Begruendung wie bei
  // list_space_intent). Ausserdem richten sich alle drei an ANBIETER, die schon
  // angemeldet sind: als Conversion gezaehlt wuerden sie die Akquise-Signale
  // verwaessern, die diese Kampagnen eigentlich suchen.
  banner_shown: CUSTOM,
  banner_cta_click: CUSTOM,
};

/**
 * Ob das Pixel geladen und Ereignisse gemeldet werden dürfen.
 *
 * - Ohne konfigurierte Pixel-ID: nein.
 * - Mit gespeicherter Entscheidung: nur bei „granted“. Das gilt auch für die
 *   Schweiz — wer über den Fusszeilen-Link widerspricht, wird nicht getrackt.
 * - Ohne Entscheidung: nur ausserhalb von EWR/UK (dort gilt die
 *   Widerspruchslösung aus Abschnitt 5 der Datenschutzerklärung).
 *
 * @returns {boolean}
 */
export const isRedditTrackingAllowed = () => {
  if (typeof window === 'undefined' || !redditPixelId) {
    return false;
  }
  const stored = readConsent();
  if (stored) {
    return stored === CONSENT_GRANTED;
  }
  return !isConsentRequiredRegion();
};

/**
 * Legt `window.rdt` als Warteschlange an, falls `pixel.js` noch nicht geladen
 * ist. Entspricht dem offiziellen Reddit-Snippet, nur lesbar ausgeschrieben:
 * Aufrufe vor dem Laden landen in `callQueue` und werden danach nachgeholt.
 *
 * @returns {Function} window.rdt
 */
const ensureRdtQueue = () => {
  if (!window.rdt) {
    const rdt = function rdt() {
      if (rdt.sendEvent) {
        rdt.sendEvent.apply(rdt, arguments);
      } else {
        rdt.callQueue.push(arguments);
      }
    };
    rdt.callQueue = [];
    window.rdt = rdt;
    // `init` MUSS der erste Eintrag der Warteschlange sein — `pixel.js` arbeitet
    // sie beim Laden der Reihe nach ab, und ein `track` vor dem `init` verfällt.
    // Deshalb sät die Warteschlange den Aufruf selbst, statt sich darauf zu
    // verlassen, dass initRedditPixel() zuerst drankommt: Seit Ereignisse auch
    // vor dem Laden gepuffert werden, kann jede der beiden Seiten zuerst hier
    // ankommen.
    rdt('init', redditPixelId);
  }
  return window.rdt;
};

/**
 * Bindet das Pixel ein und meldet den ersten Seitenaufruf. Idempotent —
 * mehrfaches Aufrufen (z. B. nach nachträglicher Einwilligung) lädt das Skript
 * nur einmal.
 *
 * @returns {boolean} true, wenn das Pixel aktiv ist
 */
export const initRedditPixel = () => {
  if (!isRedditTrackingAllowed()) {
    return false;
  }
  if (document.getElementById(SCRIPT_ID)) {
    return true;
  }

  // `init` reiht ensureRdtQueue() beim Anlegen der Warteschlange selbst ein.
  const rdt = ensureRdtQueue();
  rdt('track', 'PageVisit');

  const script = document.createElement('script');
  script.id = SCRIPT_ID;
  script.async = true;
  // Kein crossOrigin: das offizielle Snippet setzt es nicht, und ein
  // CORS-Request würde das Skript blockieren, falls redditstatic.com die
  // Header nicht liefert.
  script.src = SCRIPT_SRC;
  document.head.appendChild(script);
  return true;
};

/**
 * Reddit prüft Metadaten GEGEN DEN EREIGNISTYP und beanstandet das Ereignis,
 * wenn ein Feld dort nicht vorgesehen ist. GA4 ist grosszügiger, deshalb muss
 * die Filterung hier passieren und nicht an den Aufrufstellen.
 *
 * Alles unten ist im Events Manager (Diagnostics) gemessen, nicht geraten:
 * - 24.08.2026 — `ViewContent` mit `currency` → „Unsupported metadata", 100 %.
 * - 25.08.2026 — `ViewContent` mit `itemCount` → weiterhin „Unsupported
 *   metadata", 100 %. Reddit nennt im Beispiel jeweils nur das störende Feld;
 *   `products` blieb beide Male unbeanstandet. `itemCount` ist damit auf die
 *   Ereignisse beschränkt, bei denen eine Stückzahl überhaupt etwas bedeutet.
 * - 25.08.2026 — `listing_published` (Custom) mit `currency`, aber ohne `value`
 *   → „Missing metadata (no currency value)". Eine Währung ohne Betrag ist für
 *   Reddit unvollständig, also reisen die beiden nur noch gemeinsam.
 */
const MONETARY_REDDIT_EVENTS = new Set(['Purchase', 'AddToCart', 'Custom']);
const PRODUCT_REDDIT_EVENTS = new Set(['Purchase', 'AddToCart', 'ViewContent', 'Custom']);
const ITEM_COUNT_REDDIT_EVENTS = new Set(['Purchase', 'AddToCart']);

/**
 * Übersetzt die GA4-Parameter in das Schema des Reddit-Pixels — nur die Felder,
 * die der jeweilige Ereignistyp unterstützt. `value` ist bei beiden bereits in
 * Währungseinheiten (nicht Rappen).
 *
 * @param {string} redditEvent Reddit-Ereignisname, z. B. 'Purchase'
 * @param {Object} params GA4-Parameter
 * @returns {Object} Reddit-Parameter
 */
const toRedditParams = (redditEvent, params = {}) => {
  const out = {};
  if (MONETARY_REDDIT_EVENTS.has(redditEvent)) {
    const hasValue = typeof params.value === 'number' && !Number.isNaN(params.value);
    if (hasValue) {
      out.value = params.value;
      // `currency` NUR zusammen mit einem Betrag: alleine beanstandet Reddit es
      // als „Missing metadata (no currency value)". Betrifft z. B.
      // `listing_published`, das eine Währung, aber bewusst keinen Wert liefert
      // — der Angebotspreis eines Inserats ist kein Conversion-Umsatz und hat
      // in Reddits wertbasierter Auswertung nichts verloren.
      if (params.currency) {
        out.currency = params.currency;
      }
    }
    if (params.transaction_id) {
      out.transactionId = params.transaction_id;
      // Dedupliziert Mehrfachmeldungen derselben Buchung (Reload der
      // Erfolgsseite, Wiederholungen nach Netzfehlern).
      out.conversionId = params.transaction_id;
    }
  }
  if (PRODUCT_REDDIT_EVENTS.has(redditEvent) && params.listing_id) {
    out.products = [{ id: params.listing_id }];
    if (ITEM_COUNT_REDDIT_EVENTS.has(redditEvent)) {
      out.itemCount = 1;
    }
  }
  return out;
};

/**
 * Meldet ein semantisches GA4-Ereignis an Reddit, sofern es zugeordnet ist.
 * No-Op ohne geladenes Pixel (fehlende ID, fehlende Einwilligung, SSR).
 *
 * @param {string} event GA4-Ereignisname, z. B. 'booking_request'
 * @param {Object} [params] GA4-Parameter
 * @returns {void}
 */
export const trackRedditEvent = (event, params = {}) => {
  // Einwilligung bei JEDEM Ereignis prüfen, nicht nur beim Laden des Pixels.
  // Vorher stand hier `typeof window.rdt !== 'function'` — war das Pixel einmal
  // geladen, feuerte danach jede In-App-Navigation weiter, AUCH nach einem
  // Widerspruch; erst ein Neuladen stoppte es. Genau dieses Widerspruchsrecht
  // ist aber die tragende Begründung, das Pixel in der Schweiz zu betreiben.
  if (!isRedditTrackingAllowed()) {
    return;
  }
  const mapped = REDDIT_EVENTS[event];
  if (!mapped) {
    return;
  }
  // Warteschlange anlegen, falls `pixel.js` noch lädt. Das Skript kommt erst nach
  // `window` load plus Idle-Phase — auf Prod gemessen bei 3036 ms, während der
  // Effekt eines Direkteinstiegs schon bei der Hydration feuert. Ohne Puffer ging
  // dieses Ereignis still verloren; genau so verschwand `ViewContent` bei jedem
  // Anzeigenklick, der direkt auf einem Inserat landet. `ensureRdtQueue` ist die
  // von Reddit selbst vorgesehene Mechanik: Aufrufe sammeln sich in `callQueue`
  // und werden nachgeholt, sobald das Skript da ist. GA4 löst dasselbe Problem
  // seit jeher über den `gtag`-Stub im <head>.
  const rdt = ensureRdtQueue();
  if (mapped === CUSTOM) {
    rdt('track', 'Custom', {
      ...toRedditParams('Custom', params),
      customEventName: event,
    });
    return;
  }
  rdt('track', mapped, toRedditParams(mapped, params));
};

/**
 * Seitenaufruf nach In-App-Navigation melden. Der erste Seitenaufruf kommt
 * bereits aus `initRedditPixel`.
 *
 * @returns {void}
 */
export const trackRedditPageVisit = () => {
  // Gleiche Regel wie bei den Ereignissen: Einwilligung pro Aufruf, und eine
  // Warteschlange, falls das Pixel noch unterwegs ist.
  if (!isRedditTrackingAllowed()) {
    return;
  }
  ensureRdtQueue()('track', 'PageVisit');
};

// Nur für Tests: die Zuordnungstabelle sichtbar machen, ohne sie zu exportieren
// und damit zum Teil der öffentlichen API zu machen.
export const __testables = { REDDIT_EVENTS, CUSTOM, toRedditParams };
